import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Grid,
  useToast,
} from "@chakra-ui/react";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
  FiCheck,
} from "react-icons/fi";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box py={12} bg={useColorModeValue("gray.50", "gray.900")}>
      <Container maxW="1400px">
        <VStack spacing={12} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              bgGradient="linear(to-r, green.500, green.600)"
              bgClip="text"
            >
              Contact Us
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Need technical support or have questions about our computer components? We're here to help.
            </Text>
          </VStack>

          <Grid templateColumns={["1fr", "1fr 1fr"]} gap={8}>
            {/* Contact Information */}
            <VStack spacing={8} align="stretch">
              <Box
                p={6}
                bg={bgColor}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <VStack spacing={6} align="stretch">
                  <HStack spacing={4}>
                    <Icon as={FiMail} w={6} h={6} color="green.500" />
                    <Box>
                      <Text fontWeight="bold">Email</Text>
                      <Text color="gray.600">support@techpartshub.pk</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={4}>
                    <Icon as={FiClock} w={6} h={6} color="green.500" />
                    <Box>
                      <Text fontWeight="bold">Business Hours</Text>
                      <Text color="gray.600">
                        Monday - Saturday: 10:00 AM - 8:00 PM
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>

              <Box
                p={6}
                bg={bgColor}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="md">Technical Support</Heading>
                  <Text color="gray.600">
                    Our technical support team is available 24/7 to help you with:
                  </Text>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Icon as={FiCheck} color="green.500" />
                      <Text>Component compatibility questions</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Icon as={FiCheck} color="green.500" />
                      <Text>Build recommendations</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Icon as={FiCheck} color="green.500" />
                      <Text>Warranty claims</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Icon as={FiCheck} color="green.500" />
                      <Text>Product specifications</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            </VStack>

            {/* Contact Form */}
            <Box
              p={6}
              bg={bgColor}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Subject</FormLabel>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Message</FormLabel>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your query..."
                      rows={6}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    size="lg"
                    width="full"
                    isLoading={isSubmitting}
                    loadingText="Sending..."
                    disabled={isSuccess}
                    leftIcon={isSuccess ? <FiCheck /> : <FiSend />}
                  >
                    {isSuccess ? "Message Sent!" : "Send Message"}
                  </Button>
                </VStack>
              </form>
            </Box>
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Contact;
